import DeviceDetector from "device-detector-js";
import { EMPTY_VALUE } from "./constants";
import moment from "moment";

const deviceDetector = new DeviceDetector();

/** Asia/Kolkata — wall-clock timezone for all app MySQL DATETIME values. */
export const APP_TZ_OFFSET_MINUTES = 330;

/**
 * Parse app DATETIME strings as IST. Never use raw `new Date(mysqlString)` for comparisons.
 */
export function parseAppDateTime(value) {
    if (value == null || value === "") return NaN;
    if (value instanceof Date) return value.getTime();

    const raw = String(value).trim();
    if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
        return new Date(raw).getTime();
    }

    const dateTime = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (dateTime) {
        const [, year, month, day, hour, minute, second = "00"] = dateTime;
        return Date.parse(`${year}-${month}-${day}T${hour}:${minute}:${second}+05:30`);
    }

    const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
        return Date.parse(`${raw}T00:00:00+05:30`);
    }

    return new Date(raw).getTime();
}

/** Calendar Date whose local Y/M/D/H/M match the stored IST wall-clock digits. */
export function toAppDate(value) {
    if (value == null || value === "") return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    const raw = String(value).trim();
    const dateTime = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (dateTime) {
        const [, year, month, day, hour, minute, second = "00"] = dateTime;
        return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    }

    const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
        const [, year, month, day] = dateOnly;
        return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const ms = parseAppDateTime(value);
    return Number.isFinite(ms) ? new Date(ms) : null;
}

export function isWithinAppDateWindow({ start_at, end_at, graceMs = 0, now = Date.now() }) {
    const start = parseAppDateTime(start_at);
    const end = parseAppDateTime(end_at);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    return now >= start && now <= end + Number(graceMs || 0);
}

export function getReadableDate({ date = "", removeTime = false }) {
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
        return moment(date).format(removeTime ? "DD-MM-YY" : "DD-MM-YY HH:mm");
    }
    const ms = parseAppDateTime(date);
    if (!Number.isFinite(ms)) return EMPTY_VALUE;
    return moment(ms).utcOffset(APP_TZ_OFFSET_MINUTES).format(removeTime ? "DD-MM-YY" : "DD-MM-YY HH:mm");
}

export function getWriteableDate({ date = "", removeTime = false }) {
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
        // Calendar pickers: keep the wall-clock digits the admin selected
        return moment(date).format(removeTime ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm");
    }
    const ms = parseAppDateTime(date);
    if (!Number.isFinite(ms)) return EMPTY_VALUE;
    return moment(ms).utcOffset(APP_TZ_OFFSET_MINUTES).format(removeTime ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm");
}

export async function generateDeviceFingerprint() {
    const userAgent = navigator.userAgent;
    const device = deviceDetector.parse(userAgent);

    const type = device.device?.type || "Unknown Type";
    const brand = device.device?.brand || "Unknown Brand";
    const model = device.device?.model || "Unknown Model";
    const os = device.os?.name || "Unknown OS";
    const osVersion = device.os?.version || "";

    const screenRes = window.screen.width + window.screen.height;

    return btoa(unescape(encodeURIComponent(`${type} - ${brand} ${model} - ${os}(${osVersion}) - ${screenRes}`)));
}

export function getFilterNameFormalized(input) {
    return input.charAt(0).toUpperCase().concat(input.slice(1));
}

export function getViewIndex(items) {
    return items?.length ? items[0]?.view_index - 1 : 0;
}

export function getRandomBackgroundColor() {
    const randomColorClasses = ["bg-red-500", "bg-orange-500", "bg-green-500", "bg-blue-500", "bg-purple-500", "bg-pink-500", "bg-cyan-500", "bg-indigo-500"];
    return randomColorClasses[Math.floor(Math.random() * randomColorClasses.length)];
}

export function getFileAcceptType(type) {
    if (type === "video") {
        return ".mp4, .mkv";
    }

    if (type === "pdf") {
        return ".pdf"; // PDFs only
    }

    if (type === "sheet") {
        return ".csv"; // CSVs only
    }

    if (type === "audio") {
        return ".mp3"; // MP3 only
    }

    return ".png, .jpg, .jpeg, .gif";
}

export async function uploadMedia({
    requestPath = "/",
    file,
    onRequestStart = false,
    onResponseReceieved = false,
    onRequestFailure = false,
    onRequestEnd = false,
    parseResponseBody = true,
    onProgress,
} = {}) {
    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", process.env.REACT_APP_BACKEND_SERVER.concat(process.env.REACT_APP_MEDIA_PATH).concat(requestPath));
    if (onRequestStart) await onRequestStart();

    xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
            onProgress(Math.round((event.loaded / event.total) * 100));
        }
    };

    xhr.onload = () => {
        try {
            const jsonResponse = parseResponseBody ? JSON.parse(xhr.responseText) : xhr.responseText;
            if (onResponseReceieved) onResponseReceieved(jsonResponse, xhr.status);
        } catch (e) {
            if (onRequestFailure) onRequestFailure(e?.message);
        } finally {
            if (onRequestEnd) onRequestEnd();
        }
    };

    xhr.onerror = () => onRequestFailure("Network error");
    xhr.onabort = () => onRequestFailure("Upload aborted");

    xhr.send(formData);

    return xhr;
}

export const applyToClipBoard = (value) => navigator.clipboard.writeText(value);

export const hasRequiredAuthority = (authorities, requiredAuthority) => authorities.includes(requiredAuthority);

/** Display label for an exam: `title - subject` (legacy empty title falls back to subject / Exam #id). */
export function formatExamLabel({ title, subject_title, id } = {}) {
    const examTitle = typeof title === "string" ? title.trim() : "";
    const subjectTitle = typeof subject_title === "string" ? subject_title.trim() : "";

    if (examTitle && subjectTitle) {
        return `${examTitle} - ${subjectTitle}`;
    }
    if (examTitle) return examTitle;
    if (subjectTitle) return subjectTitle;
    return id != null ? `Exam #${id}` : "Exam";
}
